import React, { useMemo, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    MenuItem,
    Select,
    FormControl,
    Stack,
    Divider,
    Link,
    useTheme,
    Badge,
    CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./style.css";
import CustomTypography from "components/Fileds/CustomTypography";
import { statusConfigs, statusOptions } from "utils/constant";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MergedTablesPopover from "components/Fileds/CustomPopOver";
import { formatDateTime } from "utils/function";
import { WarningAmber } from "@mui/icons-material";

export default function OrderCustomerInfo({ orderDetails = {}, onStatusChange, onEditCustomer, paymentVisible = true, orderStatusVisible = true, variant, updatingStatus = false, isViewOnly = false }) {
    const data = { ...orderDetails, tableInfo: { ...orderDetails?.tableInfo, children: orderDetails?.reservationChildTables } };
    const theme = useTheme();
    const [status, setStatus] = useState(data?.status);
    const [copied, setCopied] = useState(false);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(data?.order_id);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (e) {
            // ignore
            console.error("Error in copy")
        }
    };

    const handleStatus = (e) => {
        setStatus(e.target.value);
        onStatusChange(e.target.value);
    };

    const isPending = String(data?.payment_status).toLowerCase() === "unpaid";
    let isMerged = [];

    const childIds = data?.tableReservation?.child_table_ids;

    if (typeof childIds === 'string') {
        try {
            isMerged = JSON.parse(childIds);
        } catch (e) {
            console.error('Invalid JSON string in child_table_ids:', childIds);
            isMerged = [];
        }
    } else if (Array.isArray(childIds)) {
        // already an array
        isMerged = childIds;
    } else {
        isMerged = [];
    }

    const current = data?.order_status;
    const flow = statusOptions[data?.order_type] || [];
    let next = flow[current] ? [...flow[current]] : [];
    const hasItems = Array.isArray(data?.orderItems) && data.orderItems.length > 0;

    const isPaid = data?.payment_status === "paid" ||
        (data?.paid_amount && data?.total_amount && parseFloat(data?.paid_amount) >= parseFloat(data?.total_amount));

    // If payment is already done for a delivery order, skip "delivered" layer 
    // and go straight to "completed" (labeled as "Delivered & Completed").
    if (data?.order_type === 'delivery' && isPaid) {
        if (current === 'out_for_delivery') {
            next = ['completed'];
        }
    }

    return (
        <Box className="order-customer-info-container">
            <Grid container spacing={2}>
                {/* Left Section (9 cols on lg) */}
                <Grid item xs={12} sm={6} lg={orderStatusVisible ? 9.5 : 12}>
                    <Grid container spacing={2}>
                        {/* Order ID */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <CustomTypography
                                variant={"caption"}
                                label={"Order ID"}
                                className='label'
                            />
                            <Box className='d-flex align-items-start' sx={{ gap: 1 }}>
                                <CustomTypography
                                    variant={"h6"}
                                    label={data?.order_id}
                                    className='value-label'
                                />
                                <Tooltip title={copied ? "Copied" : "Copy"} placement="top" arrow>
                                    <IconButton size="small" onClick={handleCopy} sx={{ padding: '0px !important', paddingTop: '2px !important' }}>
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Grid>

                        {/* Order Created at */}
                        <Grid item xs={12} sm={6} md={3} lg={3}>
                            <CustomTypography
                                variant={"subtitle1"}
                                label={"Order Created at"}
                                className='label'
                            />
                            <CustomTypography
                                variant={"h6"}
                                label={formatDateTime(data?.created_at)}
                                className='value-label'
                            />
                        </Grid>

                        {/* Order Accepted at */}
                        <Grid item xs={12} sm={6} md={3} lg={4}>
                            <CustomTypography
                                variant={"subtitle1"}
                                label={"Order Accepted at"}
                                className='label'
                            />
                            <CustomTypography
                                variant={"h6"}
                                // label={`${formatDateTime(data?.accepted_at)}`}
                                // label={`${formatDateTime(data?.accepted_at)}${data?.created_at === data?.accepted_at ? ' (Auto)' : ''}`}
                                label={
                                    data?.accepted_at
                                        ? `${formatDateTime(data?.accepted_at)}${data?.created_at === data?.accepted_at ? ' (Auto)' : ''}`
                                        : '-'
                                }
                                className='value-label'
                            />
                        </Grid>

                        {/* Payment Status */}
                        {paymentVisible &&
                            <Grid item xs={12} sm={6} md={2} lg={2}>
                                <CustomTypography
                                    variant={"subtitle1"}
                                    label={"Payment Status"}
                                    className='label'
                                />
                                <Box>
                                    <Chip
                                        size="small"
                                        label={data?.payment_status == "unpaid" ? "Pending" : data?.payment_status == "part_paid" ? "Partially Paid" : data?.payment_status}
                                        variant={isPending ? "outlined" : "filled"}
                                        color={isPending ? "error" : "success"}
                                        sx={{ borderRadius: '5px !important', paddingLeft: '10px', textTransform: 'capitalize' }}
                                    />
                                </Box>
                            </Grid>
                        }

                        {/* Occupied Table */}
                        {(data?.order_type !== "delivery" && data?.order_type !== "phone_order") &&
                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                <Box className='d-flex flex-column'>
                                    <CustomTypography
                                        variant={"caption"}
                                        label={"Occupied Table"}
                                        className='label'
                                    />
                                    <Box className='d-flex align-items-start' sx={{ gap: 1 }}>
                                        <IconButton size="small" sx={{ padding: '0px !important', paddingTop: '2px !important' }}>
                                            <TableRestaurantIcon fontSize="inherit" />
                                        </IconButton>
                                        <CustomTypography
                                            variant={"h6"}
                                            label={data?.table_name || data?.tableInfo?.table_no || "N/A"}
                                            className='value-label'
                                            sx={{ marginRight: '15px' }}
                                        />
                                        {isMerged?.length > 0 &&
                                            <Tooltip title={<MergedTablesPopover tableData={data?.tableInfo} type={"tableinfo"} />} arrow placement="right"
                                                enterTouchDelay={0}
                                                leaveTouchDelay={3000}
                                                interactive
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: '#1297DE',
                                                            color: '#fff',
                                                            p: '10px',
                                                            marginLeft: '30px !important',
                                                            width: '300px',
                                                            top: '10px',
                                                            borderRadius: '8px',
                                                            '& .MuiTooltip-arrow': {
                                                                color: '#1297DE',
                                                                fontSize: 20, // <-- controls arrow size
                                                            },
                                                        }
                                                    },
                                                }}
                                            >
                                                <Badge
                                                    badgeContent={`+${isMerged?.length}`}
                                                    sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', backgroundColor: '#1297DE', color: '#fff', padding: '4px 8px', top: '10px' } }}
                                                />
                                            </Tooltip>
                                        }
                                    </Box>
                                </Box>
                            </Grid>}

                        {/* Number of Persons */}
                        {(data?.order_type !== "delivery" && data?.order_type !== "phone_order") &&
                            <Grid item xs={12} sm={6} md={3} lg={3}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Number of Persons"}
                                    className='label'
                                />
                                <Box className='d-flex align-items-start' sx={{ gap: 1 }}>
                                    <IconButton size="small" sx={{ padding: '0px !important', paddingTop: '2px !important' }}>
                                        <PeopleAltIcon fontSize="inherit" />
                                    </IconButton>
                                    <CustomTypography
                                        variant={"h6"}
                                        label={data?.guest_count || "N/A"}
                                        className='value-label'
                                        sx={{ marginRight: '15px' }}
                                    />
                                </Box>
                            </Grid>
                        }

                        {/* Rider Information */}
                        {(data?.order_type == "delivery" || data?.order_type == "phone_order") && data?.riderInfo && (
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Rider Details"}
                                    className='label'
                                />
                                <CustomTypography
                                    variant={"h6"}
                                    label={`${data?.riderInfo?.full_name} (+${data?.riderInfo?.country_code}${data?.riderInfo?.phone})`}
                                    className='value-label'
                                    sx={{ marginRight: '15px' }}
                                />
                            </Grid>
                        )}

                        {/* Delivery Address */}
                        {(data?.order_type === "delivery" || data?.order_type === "phone_order") && data?.orderAddress && (
                            <Grid item xs={12} sm={6} md={6} lg={3}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Delivery Address"}
                                    className='label'
                                />
                                <CustomTypography
                                    variant={"h6"}
                                    label={
                                        data?.order_type === "phone_order"
                                            ? [
                                                data.orderAddress?.building_number,
                                                data.orderAddress?.building_name,
                                                data.orderAddress?.address,
                                                data.orderAddress?.locality,
                                                data.orderAddress?.zipcode
                                            ].filter(Boolean).join(', ')
                                            : [
                                                data.orderAddress?.address,
                                                data.orderAddress?.locality,
                                                data.orderAddress?.zipcode
                                            ].filter(Boolean).join(', ')
                                    }
                                    className='value-label'
                                    sx={{ marginRight: '15px', wordBreak: 'break-word' }}
                                />
                            </Grid>
                        )}

                        {/* Customer Details */}
                        <Grid item xs={12} sm={6} md={6} lg={4}>
                            <Box className='d-flex align-items-center' sx={{ gap: 1 }}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Customer Details"}
                                    className='label'
                                />
                                {orderStatusVisible && !isViewOnly && <Link
                                    component="button"
                                    type="button"
                                    onClick={onEditCustomer}
                                    sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary" }}
                                >
                                    EDIT
                                </Link>}
                            </Box>
                            <CustomTypography
                                variant={"h6"}
                                label={
                                    (() => {
                                        const name = data?.customerVendor?.customer_full_name || data?.customer_name;
                                        const countryCode = data?.customerVendor?.customerInfo?.country_code || '';
                                        const phone = data?.customerVendor?.customerInfo?.phone || data?.customer_mobile;
                                        if (!name && !phone) return "-";
                                        const phoneStr = phone ? `(${countryCode}${phone})` : '';
                                        return [name, phoneStr].filter(Boolean).join(" ");
                                    })()
                                }
                                className='value-label'
                                sx={{ marginRight: '15px' }}
                            />
                        </Grid>

                        {/* Payment Mode */}
                        {data?.payment_status != "unpaid" &&
                            <Grid item xs={12} sm={6} md={2} lg={2}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Payment Mode"}
                                    className='label'
                                />
                                <CustomTypography
                                    variant={"h6"}
                                    label={data?.payment_mode ? data?.payment_mode : "-"}
                                    className='value-label'
                                    sx={{ marginRight: '15px', textTransform: 'capitalize' }}
                                />
                            </Grid>
                        }

                         {(data?.order_type === "delivery" || data?.order_type === "phone_order") && data?.delivered_at && (
                            <Grid item xs={12} sm={6} md={6} lg={2}>
                                <CustomTypography
                                    variant={"caption"}
                                    label={"Delivered At"}
                                    className='label'
                                />
                                <CustomTypography
                                    variant={"h6"}
                                    label={
                                        formatDateTime(data?.delivered_at) || data?.delivered_at || "-"
                                    }
                                    className='value-label'
                                    sx={{ marginRight: '15px', wordBreak: 'break-word' }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Right Section (3 cols on lg) */}
                {orderStatusVisible && (
                    <Grid item xs={12} sm={6} md={2} lg={2}>
                        <CustomTypography
                            variant={"caption"}
                            label={"Order Status"}
                            className='label'
                            sx={{ display: 'block' }}
                        />
                        {isViewOnly ? (
                            // View-only styled box (looks like Select but not interactive)
                            <Box
                                sx={{
                                    backgroundColor: statusConfigs[current]?.bg,
                                    color: "#000",
                                    borderRadius: "8px",
                                    py: "4px",
                                    px: "8px",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    height: 32,
                                    border: "1px solid transparent",
                                }}
                            >
                                {statusConfigs[current]?.label || "Select status"}
                            </Box>
                        ) : (
                            <Select
                                value={current || ""}
                                size="small"
                                IconComponent={
                                    updatingStatus
                                        ? () => (
                                            <CircularProgress
                                                size={16}
                                                sx={{ color: "#000", position: "absolute", right: "7px" }}
                                            />
                                        )
                                        : ArrowDropDownIcon
                                }
                                renderValue={() => {
                                    if (data?.order_type === 'delivery' && isPaid && current === 'completed') {
                                        return "Delivered & Completed";
                                    }
                                    return statusConfigs[current]?.label || "Select status"
                                }}
                                disabled={!hasItems}
                                onChange={(e) => onStatusChange(e.target.value)}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: statusConfigs[current]?.bg,
                                        color: "#000",
                                        borderRadius: "8px",
                                        "& fieldset": { border: "none" },
                                    },
                                    "& .MuiSelect-select": {
                                        py: "4px",
                                        px: "8px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        backgroundColor: statusConfigs[current]?.bg,
                                        color: "#000",
                                    },
                                    width: "100%",
                                }}
                                MenuProps={{
                                    PaperProps: { sx: { mt: 1, } },
                                }}
                            >
                                {next.map((statusKey) => {
                                    let optionLabel = statusConfigs[statusKey]?.optionLabel;
                                    if (data?.order_type === 'delivery' && isPaid && statusKey === 'completed') {
                                        optionLabel = "Delivered & Completed";
                                    }
                                    return (
                                        <MenuItem key={statusKey} value={statusKey}>
                                            {optionLabel}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        )}
                        {!hasItems && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ mt: 1 }}
                            >
                                <WarningAmber color="warning" fontSize="small" />
                                <Typography variant="caption" color="warning.main">
                                    Status will be changeable after adding items to the order.
                                </Typography>
                            </Stack>
                        )}
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}
